import { useEffect, useRef, useState, useCallback } from "react";
import ApiRequest from "../../../../../api/ApiRequest";
import { API_ROUTES, API_AUTH_TYPES } from "../../../../../api/apiConstants";

// Lightweight helper to manage a WebRTC session with OpenAI Realtime
export default function useOpenAIWebRTC({ role = API_AUTH_TYPES.ADVISOR, model = "gpt-realtime", voice = "alloy", conversationId = null, onUserTranscript = null, onAssistantTranscript = null } = {}) {
    const [connected, setConnected] = useState(false);
    const [error, setError] = useState(null);
    const pcRef = useRef(null);
    const dcRef = useRef(null);
    const remoteAudioRef = useRef(null);
    const localStreamRef = useRef(null);
    const locationHeaderRef = useRef("");
    // Track server-created conversation_id so subsequent logs and socket emits include it
    const activeConversationIdRef = useRef(conversationId || null);
    useEffect(() => { activeConversationIdRef.current = conversationId || null; }, [conversationId]);
    // Suppress UI logging when seeding past conversation into the realtime session
    const hydratingRef = useRef(false);
    const getToken = useCallback(async () => {
        const parabotApi = new ApiRequest(API_ROUTES.PARABOT, role);
        const endpoint = "realtime_token"; // auth suffix handled by ApiRequest
        const infoStr = localStorage.getItem('parabot_info');
        let info; try { info = infoStr ? JSON.parse(infoStr) : null; } catch (_) { }
        const fallbackLocation = (typeof window !== 'undefined' && window.location && typeof window.location.pathname === 'string') ? window.location.pathname : '';
        let fallbackTimeZone = '';
        try { fallbackTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch (_) { }
        // Use the conversation_id directly from the prop
        console.log('[PARABOT-VOICE] getToken with conversation_id:', conversationId);
        const queryParams = {
            model,
            voice,
            location: info?.location ?? fallbackLocation,
            user_time_zone: info?.user_time_zone ?? fallbackTimeZone,
        };
        if (conversationId != null) queryParams.conversation_id = conversationId;
        const data = await parabotApi.get({ endpoint, queryParams });
        return data;
    }, [role, model, voice, conversationId]);

    const attachDataChannelHandlers = useCallback(() => {
        const dc = dcRef.current;
        if (!dc) return;

        const parabotApi = new ApiRequest(API_ROUTES.PARABOT, role);
        const sendJSON = (obj) => {
            try { dc.send(JSON.stringify(obj)); } catch (_) { }
        };
        const syncConversationId = (id) => {
            try {
                if (!id) return;
                activeConversationIdRef.current = id;
                // Notify UI locally via DOM event (no server roundtrip required)
                if (typeof window !== 'undefined' && window.dispatchEvent) {
                    try { window.dispatchEvent(new CustomEvent('parabot:voice-conversation-id', { detail: { conversation_id: id } })); } catch (_) { }
                }
                // Best-effort also via socket for any server-driven listeners
                if (window.parabotSocket) { try { window.parabotSocket.emit('voice-conversation-id', { conversation_id: id }); } catch (_) { } }
            } catch (_) { }
        };

        // Ensure tools are synced into the session
        dc.addEventListener("open", async () => {
            try {
                const toolsResp = await parabotApi.get({ endpoint: "realtime_tools" });
                if (toolsResp && Array.isArray(toolsResp.tools)) {
                    sendJSON({
                        type: "session.update",
                        session: {
                            tools: toolsResp.tools,
                            tool_choice: "auto",
                            input_audio_transcription: {
                                model: "whisper-1"
                            }
                        }
                    });
                }
                // Skipping data-channel hydration: conversation context is seeded via
                // server-side instructions when minting the Realtime token.
            } catch (e) {
                console.error("Failed to push tools to Realtime session", e);
            }
        });

        const handleFunctionCalls = async (serverEvent) => {
            try {
                const output = serverEvent?.response?.output;
                if (!Array.isArray(output)) return;
                const functionCalls = output.filter(item => item?.type === "function_call");
                if (functionCalls.length > 0) {
                    try { console.log('[PARABOT-VOICE] Detected function calls:', functionCalls.map(fc => ({ name: fc.name, call_id: fc.call_id }))); } catch (_) { }
                }
                for (const fc of functionCalls) {
                    const callId = fc.call_id;
                    const name = fc.name;
                    let args = {};
                    try { args = fc.arguments ? JSON.parse(fc.arguments) : {}; } catch (_) { }
                    try {
                        try { console.log('[PARABOT-VOICE] Emitting tool_start', { tool_id: callId, tool: name }); } catch (_) { }
                        // Surface tool activity to UI
                        try {
                            if (window.parabotSocket) {
                                // Tool started
                                const startPayload = { tool_id: callId, tool: name };
                                window.parabotSocket.emit('tool_start', startPayload);
                                try { window.dispatchEvent(new CustomEvent('parabot:tool_start', { detail: startPayload })); } catch (_) { }
                                // Stream input (single delta with full JSON payload)
                                try { console.log('[PARABOT-VOICE] Emitting tool_input_delta'); } catch (_) { }
                                if (typeof fc.arguments === 'string' && fc.arguments.length > 0) {
                                    const deltaPayload = { tool_id: callId, accumulated_json: fc.arguments };
                                    window.parabotSocket.emit('tool_input_delta', deltaPayload);
                                    try { window.dispatchEvent(new CustomEvent('parabot:tool_input_delta', { detail: deltaPayload })); } catch (_) { }
                                } else {
                                    const deltaPayload = { tool_id: callId, accumulated_json: JSON.stringify(args) };
                                    window.parabotSocket.emit('tool_input_delta', deltaPayload);
                                    try { window.dispatchEvent(new CustomEvent('parabot:tool_input_delta', { detail: deltaPayload })); } catch (_) { }
                                }
                                // Finalize input (tool_call)
                                try { console.log('[PARABOT-VOICE] Emitting tool_call'); } catch (_) { }
                                const callPayload = { tool_id: callId, arguments: fc.arguments || JSON.stringify(args), tool: name };
                                window.parabotSocket.emit('tool_call', callPayload);
                                try { window.dispatchEvent(new CustomEvent('parabot:tool_call', { detail: callPayload })); } catch (_) { }
                            }
                        } catch (_) { }

                        const execResp = await parabotApi.post({ endpoint: "realtime_tool_call", body: { name, arguments: args } });
                        const toolOutput = execResp?.output ?? execResp;
                        try { console.log('[PARABOT-VOICE] Tool execution complete', { name, callId, outputPreview: typeof toolOutput === 'string' ? toolOutput.slice(0, 120) : JSON.stringify(toolOutput).slice(0, 120) }); } catch (_) { }
                        // Send the function call result back to the model
                        sendJSON({
                            type: "conversation.item.create",
                            item: {
                                type: "function_call_output",
                                call_id: callId,
                                output: JSON.stringify(toolOutput)
                            }
                        });
                        // Ask the model to continue
                        sendJSON({ type: "response.create" });
                        // Emit result to UI
                        try {
                            if (window.parabotSocket) {
                                try { console.log('[PARABOT-VOICE] Emitting tool_result'); } catch (_) { }
                                const resultPayload = { tool_use_id: callId, result: toolOutput, tool: name };
                                window.parabotSocket.emit('tool_result', resultPayload);
                                try { window.dispatchEvent(new CustomEvent('parabot:tool_result', { detail: resultPayload })); } catch (_) { }
                            }
                        } catch (_) { }
                        // Log a lightweight assistant line to keep trace
                        try {
                            const body = {
                                conversation_id: activeConversationIdRef.current || null,
                                role: 'assistant',
                                message: { content: [{ type: 'text', text: `Used tool: ${name}` }] }
                            };
                            const resp = await parabotApi.post({ endpoint: 'realtime_log_message', body });
                            if (resp && resp.conversation_id) syncConversationId(resp.conversation_id);
                        } catch (_) { }
                    } catch (toolErr) {
                        console.error("Tool execution error", toolErr);
                        // Return an error result so the model can recover
                        sendJSON({
                            type: "conversation.item.create",
                            item: {
                                type: "function_call_output",
                                call_id: callId,
                                output: JSON.stringify({ error: { code: "tool_execution_failed", message: toolErr?.message || "tool_execution_failed" } })
                            }
                        });
                        sendJSON({ type: "response.create" });
                    }
                }
            } catch (e) {
                console.error("handleFunctionCalls error", e);
            }
        };

        const onMessage = async (e) => {
            try {
                const serverEvent = JSON.parse(e.data);
                console.log('[PARABOT-VOICE] Received event:', serverEvent?.type);
                if (serverEvent?.type === 'error') {
                    try { console.error('[PARABOT-VOICE] Error event payload:', serverEvent); } catch (_) { }
                }

                // Handle function calls
                if (serverEvent?.type === "response.done") {
                    await handleFunctionCalls(serverEvent);
                }

                // Indicate loading lifecycle for UI
                if (serverEvent?.type === "response.created") {
                    try {
                        if (window.parabotSocket) window.parabotSocket.emit('voice-loading-start');
                        try { window.dispatchEvent(new CustomEvent('parabot:voice-loading-start')); } catch (_) { }
                    } catch (_) { }
                }
                if (serverEvent?.type === "response.done") {
                    try {
                        if (window.parabotSocket) window.parabotSocket.emit('voice-loading-stop');
                        try { window.dispatchEvent(new CustomEvent('parabot:voice-loading-stop')); } catch (_) { }
                    } catch (_) { }
                }

                // Log user input when conversation item is added (likely contains user transcript)
                if (serverEvent?.type === "conversation.item.added") {
                    try {
                        console.log('[PARABOT-VOICE] Raw conversation.item.added event:', serverEvent);
                        const item = serverEvent?.item;
                        if (item?.type === "message" && item?.role === "user") {
                            if (hydratingRef.current) {
                                // Skip UI logging/emit while seeding past messages
                                console.log('[PARABOT-VOICE] Skipping user item during hydration');
                                return;
                            }
                            // Extract text from user message content
                            const content = Array.isArray(item?.content) ? item.content : [];
                            const textContent = content.find(c => c?.type === "input_text" || c?.type === "text");
                            const transcript = textContent?.text;

                            if (transcript && transcript.trim()) {
                                console.log('[PARABOT-VOICE] User transcript from conversation item:', transcript);
                                const body = {
                                    conversation_id: activeConversationIdRef.current || null,
                                    role: 'user',
                                    message: { content: [{ type: 'text', text: transcript }] }
                                };
                                console.log('[PARABOT-VOICE] Logging user message:', body);
                                const resp = await parabotApi.post({ endpoint: 'realtime_log_message', body });
                                if (resp && resp.conversation_id) syncConversationId(resp.conversation_id);

                                // Emit to Socket.IO for UI display
                                if (window.parabotSocket) {
                                    console.log('[PARABOT-VOICE] Emitting voice-user-message to socket');
                                    window.parabotSocket.emit('voice-user-message', { conversation_id: activeConversationIdRef.current, text: transcript, role: 'user' });
                                } else {
                                    console.warn('[PARABOT-VOICE] window.parabotSocket not available');
                                }
                                // Notify UI via optional callback
                                try {
                                    if (onUserTranscript && typeof onUserTranscript === 'function') {
                                        onUserTranscript({ text: transcript, conversation_id: activeConversationIdRef.current });
                                    }
                                } catch (_) { }
                            } else {
                                console.log('[PARABOT-VOICE] No text found in user conversation item');
                            }
                        }
                    } catch (err) {
                        console.error('[PARABOT-VOICE] Error handling conversation.item.added:', err);
                    }
                }

                // Log assistant speech transcript when available (correct event name from logs)
                if (serverEvent?.type === "response.output_audio_transcript.done") {
                    try {
                        console.log('[PARABOT-VOICE] Raw assistant transcript event:', serverEvent);
                        const transcript = serverEvent?.transcript;
                        if (transcript && transcript.trim()) {
                            console.log('[PARABOT-VOICE] Assistant transcript:', transcript);
                            const body = {
                                conversation_id: activeConversationIdRef.current || null,
                                role: 'assistant',
                                message: { content: [{ type: 'text', text: transcript }] }
                            };
                            console.log('[PARABOT-VOICE] Logging assistant message:', body);
                            const resp = await parabotApi.post({ endpoint: 'realtime_log_message', body });
                            if (resp && resp.conversation_id) syncConversationId(resp.conversation_id);

                            // Emit to Socket.IO for UI display
                            if (window.parabotSocket) {
                                console.log('[PARABOT-VOICE] Emitting voice-assistant-message to socket');
                                window.parabotSocket.emit('voice-assistant-message', { conversation_id: activeConversationIdRef.current, text: transcript, role: 'assistant' });
                            } else {
                                console.warn('[PARABOT-VOICE] window.parabotSocket not available');
                            }
                            // Notify UI via optional callback
                            try {
                                if (onAssistantTranscript && typeof onAssistantTranscript === 'function') {
                                    onAssistantTranscript({ text: transcript, conversation_id: activeConversationIdRef.current });
                                }
                            } catch (_) { }
                        } else {
                            console.log('[PARABOT-VOICE] Empty or missing assistant transcript');
                        }
                    } catch (err) {
                        console.error('[PARABOT-VOICE] Error handling assistant transcription:', err);
                    }
                }

                // User speech transcription events: handle both delta and completed
                if (serverEvent?.type && serverEvent.type.includes('input_audio_transcription')) {
                    try {
                        const isCompleted = serverEvent.type.endsWith('.completed');
                        const textCandidate = typeof serverEvent.transcript === 'string' && serverEvent.transcript.trim() ? serverEvent.transcript : (typeof serverEvent.delta === 'string' ? serverEvent.delta : '');
                        if (textCandidate && textCandidate.trim()) {
                            console.log('[PARABOT-VOICE] User transcript event:', serverEvent.type, textCandidate);
                            // Only persist and surface on completed to avoid duplicates
                            if (isCompleted) {
                                const body = {
                                    conversation_id: activeConversationIdRef.current || null,
                                    role: 'user',
                                    message: { content: [{ type: 'text', text: textCandidate }] }
                                };
                                const resp = await parabotApi.post({ endpoint: 'realtime_log_message', body });
                                if (resp && resp.conversation_id) syncConversationId(resp.conversation_id);
                                if (window.parabotSocket) {
                                    window.parabotSocket.emit('voice-user-message', { conversation_id: activeConversationIdRef.current, text: textCandidate, role: 'user' });
                                }
                                // Optional UI callback
                                try { if (onUserTranscript && typeof onUserTranscript === 'function') onUserTranscript({ text: textCandidate, conversation_id: activeConversationIdRef.current }); } catch (_) { }
                            }
                        }
                    } catch (err) {
                        console.error('[PARABOT-VOICE] Error handling user transcription:', err);
                    }
                }

                // Legacy: Persist assistant text turns when done (fallback)
                if (serverEvent?.type === "response.done") {
                    try {
                        const textOutput = Array.isArray(serverEvent?.response?.output)
                            ? serverEvent.response.output.find(o => o?.type === 'message')
                            : null;
                        const textParts = Array.isArray(textOutput?.content)
                            ? textOutput.content.filter(c => c?.type === 'output_text')
                            : [];
                        const finalText = textParts.map(p => p?.text).filter(Boolean).join(' ').trim();
                        if (finalText) {
                            console.log('[PARABOT-VOICE] Legacy response text:', finalText);
                            const body = {
                                conversation_id: activeConversationIdRef.current || null,
                                role: 'assistant',
                                message: { content: [{ type: 'text', text: finalText }] }
                            };
                            const resp = await parabotApi.post({ endpoint: 'realtime_log_message', body });
                            if (resp && resp.conversation_id) syncConversationId(resp.conversation_id);
                            // Notify UI via optional callback
                            try {
                                if (onAssistantTranscript && typeof onAssistantTranscript === 'function') {
                                    onAssistantTranscript({ text: finalText, conversation_id: activeConversationIdRef.current });
                                }
                            } catch (_) { }
                        }
                    } catch (err) {
                        console.error('[PARABOT-VOICE] Error handling legacy response:', err);
                    }
                }
            } catch (err) {
                console.error('[PARABOT-VOICE] Error in onMessage:', err);
            }
        };

        dc.addEventListener("message", onMessage);
    }, [role, onUserTranscript, onAssistantTranscript]);

    const disconnect = useCallback(() => {
        try {
            if (dcRef.current) {
                try { dcRef.current.close(); } catch (_) { }
                dcRef.current = null;
            }
            if (pcRef.current) {
                try { pcRef.current.close(); } catch (_) { }
                pcRef.current = null;
            }
            if (localStreamRef.current) {
                localStreamRef.current.getTracks().forEach(t => t.stop());
                localStreamRef.current = null;
            }
            setConnected(false);
        } catch (_) { }
    }, []);

    const connect = useCallback(async () => {
        try {
            setError(null);

            // 1) Mint ephemeral token
            const tokenResponse = await getToken();
            const EPHEMERAL_KEY = tokenResponse?.client_secret?.value || tokenResponse?.value;
            if (!EPHEMERAL_KEY) throw new Error("Failed to obtain ephemeral key");

            // 2) Create peer connection
            const pc = new RTCPeerConnection();
            pcRef.current = pc;

            // Remote audio output
            const audioEl = document.createElement("audio");
            audioEl.autoplay = true;
            remoteAudioRef.current = audioEl;
            pc.ontrack = (e) => {
                audioEl.srcObject = e.streams[0];
            };

            // Local mic input
            const ms = await navigator.mediaDevices.getUserMedia({ audio: true });
            localStreamRef.current = ms;
            pc.addTrack(ms.getTracks()[0]);

            // Data channel for events
            const dc = pc.createDataChannel("oai-events");
            dcRef.current = dc;
            attachDataChannelHandlers();

            // 3) SDP offer/answer with OpenAI
            const offer = await pc.createOffer();
            await pc.setLocalDescription(offer);

            const baseUrl = "https://api.openai.com/v1/realtime/calls";
            const sdpResponse = await fetch(`${baseUrl}?model=${encodeURIComponent(model)}`, {
                method: "POST",
                body: offer.sdp,
                headers: {
                    Authorization: `Bearer ${EPHEMERAL_KEY}`,
                    "Content-Type": "application/sdp",
                },
            });

            const location = sdpResponse.headers.get("Location");
            if (location) {
                try {
                    const callId = location.split("/").pop();
                    locationHeaderRef.current = callId || "";
                } catch (_) { }
            }

            const answer = { type: "answer", sdp: await sdpResponse.text() };
            await pc.setRemoteDescription(answer);
            setConnected(true);
            return { audioElement: audioEl, dataChannel: dc, peerConnection: pc };
        } catch (e) {
            console.error("WebRTC connect error", e);
            setError(e?.message || "Failed to connect");
            disconnect();
            throw e;
        }
    }, [getToken, model, attachDataChannelHandlers, disconnect]);

    const sendEvent = useCallback((event) => {
        if (dcRef.current && dcRef.current.readyState === "open") {
            dcRef.current.send(JSON.stringify(event));
        }
    }, []);

    useEffect(() => () => disconnect(), [disconnect]);

    return {
        connect,
        disconnect,
        sendEvent,
        connected,
        error,
        remoteAudioRef,
        callId: locationHeaderRef.current,
    };
}


